import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Music2, Trash2, ArrowUp, ArrowDown, Upload } from "lucide-react";
import { adminApi } from "@/lib/api";

const CHUNK = 5 * 1024 * 1024;
const fmtDur = (s) => (Number.isFinite(s) && s > 0 ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "—");
const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const readDuration = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const a = new Audio();
    a.preload = "metadata";
    a.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Number.isFinite(a.duration) ? a.duration : null); };
    a.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    a.src = url;
  });

export default function SoundtrackCard({ token }) {
  const [tracks, setTracks] = useState([]);
  const [queue, setQueue] = useState([]); // [{name, pct, status}]
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => adminApi(token).get("/soundtrack").then((r) => setTracks(r.data || [])).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);

  const uploadOne = async (file, update) => {
    const uploadId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `snd_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    const duration = await readDuration(file);
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(i));
      fd.append("total", String(total));
      fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), `${uploadId}.${i}`);
      await adminApi(token).post("/admin/upload-video/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(Math.round(((i + 1) / total) * 95));
    }
    await adminApi(token).post("/admin/soundtrack/finalize", {
      upload_id: uploadId,
      total,
      filename: file.name,
      content_type: file.type || "",
      title: file.name.replace(/\.[^.]+$/, ""),
      duration_sec: duration,
    });
    update(100);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setQueue(files.map((f) => ({ name: f.name, pct: 0, status: "waiting" })));
    let okCount = 0;
    for (let i = 0; i < files.length; i++) {
      const update = (pct, status = "uploading") =>
        setQueue((q) => q.map((row, j) => (j === i ? { ...row, pct, status } : row)));
      try {
        update(0);
        await uploadOne(files[i], update);
        update(100, "done");
        okCount += 1;
        load();
      } catch (err) {
        update(0, "error");
        toast.error(`${files[i].name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }
    setBusy(false);
    if (okCount) toast.success(`${okCount} song${okCount === 1 ? "" : "s"} added to the soundtrack`);
    setTimeout(() => setQueue([]), 2500);
  };

  const edit = async (t, patch) => {
    try {
      await adminApi(token).put(`/admin/soundtrack/${t.id}`, patch);
      setTracks((ts) => ts.map((x) => (x.id === t.id ? { ...x, ...patch } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tracks.length) return;
    const next = [...tracks];
    [next[i], next[j]] = [next[j], next[i]];
    setTracks(next);
    try {
      await adminApi(token).post("/admin/soundtrack/reorder", { ids: next.map((t) => t.id) });
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  const del = async (t) => {
    if (!window.confirm(`Remove "${t.title}" from the soundtrack?`)) return;
    try {
      await adminApi(token).delete(`/admin/soundtrack/${t.id}`);
      setTracks((ts) => ts.filter((x) => x.id !== t.id));
      toast.success("Song removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-4" data-testid="soundtrack-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-stone-800">Soundtrack (Music Player)</h3>
          <p className="text-xs text-stone-600 mt-1">
            Songs show up in the <b>Our Soundtrack</b> player on the storefront. Nothing auto-plays — guests tap to listen and can skip between songs.
            Files are kept exactly as uploaded (MP3, WAV, FLAC, M4A/AAC, OGG — up to 300&nbsp;MB each) and streamed with download blocked. No limit on how many you add.
          </p>
        </div>
        <span className="shrink-0 text-xs font-bold text-stone-500 bg-stone-100 rounded-full px-2.5 py-1" data-testid="soundtrack-count">
          {tracks.length} song{tracks.length === 1 ? "" : "s"}
        </span>
      </div>

      <label
        className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-5 cursor-pointer text-sm font-bold ${
          busy ? "border-stone-300 text-stone-400" : "border-brand-red/50 text-brand-red hover:bg-brand-red/5"
        }`}
        data-testid="soundtrack-upload-label"
      >
        <Upload className="w-4 h-4" />
        {busy ? "Uploading…" : "Add songs (pick one or many)"}
        <input type="file" accept="audio/*,.mp3,.wav,.flac,.m4a,.aac,.ogg" multiple className="hidden" disabled={busy} onChange={onFiles} data-testid="soundtrack-upload-input" />
      </label>

      {queue.length > 0 && (
        <ul className="space-y-1" data-testid="soundtrack-upload-queue">
          {queue.map((q, i) => (
            <li key={i} className="text-xs">
              <div className="flex justify-between gap-2">
                <span className="truncate">{q.name}</span>
                <span className={q.status === "error" ? "text-red-600" : "text-stone-500"}>
                  {q.status === "error" ? "failed" : q.status === "done" ? "done" : q.status === "waiting" ? "waiting" : `${q.pct}%`}
                </span>
              </div>
              <div className="h-1.5 rounded bg-stone-200 overflow-hidden mt-1">
                <div className="h-full bg-brand-red transition-[width]" style={{ width: `${q.pct}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {tracks.length === 0 ? (
        <div className="text-sm text-stone-500 flex items-center gap-2" data-testid="soundtrack-empty">
          <Music2 className="w-4 h-4" /> No songs yet — the storefront player stays hidden until you add one.
        </div>
      ) : (
        <ol className="divide-y divide-stone-200 rounded-xl border border-stone-200 overflow-hidden" data-testid="soundtrack-admin-list">
          {tracks.map((t, i) => (
            <li key={t.id} className="p-3 flex gap-3 items-start bg-white" data-testid={`soundtrack-admin-row-${t.id}`}>
              <span className="w-6 text-xs text-stone-400 pt-2.5 tabular-nums">{i + 1}</span>
              <div className="flex-1 min-w-0 space-y-1.5">
                <input
                  key={`${t.id}-${t.title}`}
                  defaultValue={t.title}
                  onBlur={(e) => {
                    const v = e.target.value.trim() || "Untitled";
                    if (v !== t.title) edit(t, { title: v });
                    else e.target.value = t.title;
                  }}
                  placeholder="Song title"
                  className="w-full px-2 py-1.5 rounded border border-stone-300 text-sm font-semibold"
                  data-testid={`soundtrack-title-input-${t.id}`}
                />
                <input
                  defaultValue={t.artist}
                  onBlur={(e) => e.target.value.trim() !== (t.artist || "") && edit(t, { artist: e.target.value.trim() })}
                  placeholder="Artist (optional)"
                  className="w-full px-2 py-1.5 rounded border border-stone-300 text-xs"
                  data-testid={`soundtrack-artist-input-${t.id}`}
                />
                <div className="text-[11px] text-stone-500">
                  {fmtDur(t.duration_sec)} · {fmtSize(t.size)} · {(t.content_type || "").replace("audio/", "").toUpperCase()}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`soundtrack-up-${t.id}`}
                  className="w-8 h-8 rounded border border-stone-300 flex items-center justify-center disabled:opacity-30 hover:bg-stone-100">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === tracks.length - 1} aria-label="Move down" data-testid={`soundtrack-down-${t.id}`}
                  className="w-8 h-8 rounded border border-stone-300 flex items-center justify-center disabled:opacity-30 hover:bg-stone-100">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => del(t)} aria-label="Delete" data-testid={`soundtrack-delete-${t.id}`}
                  className="w-8 h-8 rounded border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
